import _ from 'underscore';

import { navRefreshPageIds, getSiteMapItemRoute } from '@bingads-webui-react/with-site-map';
import { ToolsDialogConfig } from '@bingads-webui-react/tools-dialog';

import {
  I18n, Scope, SitemapItem, UserContext,
} from '../types';

interface ToolsDialogGroupConfig {
  id: string;
  name: string;
  items: string[];
}

const getToolsDialogGroups = (permissions: any) : ToolsDialogGroupConfig[] => {
  const assetsManagementEntry = permissions.dynamic.AdsStudioMVP ? navRefreshPageIds.adStudio : navRefreshPageIds.assetLibrary;
  return [
    {
      id: 'advertising-and-tracking',
      name: _TL_('Advertising, targeting, and tracking'),
      items: [
        navRefreshPageIds.advertise,
        navRefreshPageIds.audiences,
        navRefreshPageIds.budgetAndBid,
        navRefreshPageIds.conversions,
        navRefreshPageIds.keywords,
        navRefreshPageIds.preview,
        navRefreshPageIds.performanceTargets,
        navRefreshPageIds.websiteExclusion,
        navRefreshPageIds.brandSafetyControls,
      ],
    },
    {
      id: 'content-and-data',
      name: _TL_('Content and data'),
      items: [
        assetsManagementEntry,
        navRefreshPageIds.brands,
        navRefreshPageIds.productFeed,
        navRefreshPageIds.businessLocation,
        navRefreshPageIds.hotel,
        navRefreshPageIds.hotelV2,
        navRefreshPageIds.merchantCenter,
        navRefreshPageIds.microsoftStoreAds,
        navRefreshPageIds.brandLists,
        navRefreshPageIds.smartPage,
      ],
    },
    {
      id: 'import-and-bulk',
      name: _TL_('Import and other bulk actions'),
      items: [
        navRefreshPageIds.bulkActions,
        navRefreshPageIds.businessData,
        navRefreshPageIds.imports,
        navRefreshPageIds.labels,
        navRefreshPageIds.advertisingEditor,
      ],
    },
    {
      id: 'measure-results',
      name: _TL_('Measure results'),
      items: [
        navRefreshPageIds.reports,
        navRefreshPageIds.clarity,
        navRefreshPageIds.advertisingMobile,
        navRefreshPageIds.liftMeasurement,
      ],
    },
    {
      id: 'account-and-billing',
      name: _TL_('Account and billing management'),
      items: [
        navRefreshPageIds.allAccounts,
        navRefreshPageIds.accountMap,
        navRefreshPageIds.billing,
      ],
    },
  ];
};

interface GetToolsdialogConfigParams {
  i18n: I18n;
  siteMap: SitemapItem[];
  scope: Scope;
  userContext: UserContext;
  permissions: any;
  onClick: ({
    // eslint-disable-next-line no-unused-vars
    id, isPinned, isPinnable, route,
  }) => void;
}

export const getToolsDialogConfig = (params: GetToolsdialogConfigParams): ToolsDialogConfig => {
  const {
    i18n,
    siteMap,
    scope,
    userContext,
    permissions,
    onClick,
  } = params;

  const toolsDialogGroups = getToolsDialogGroups(permissions);
  const isNavRefresh2Enabled = permissions && permissions.dynamic && permissions.dynamic.NavigationRefreshTools;

  const groups = _.map(
    toolsDialogGroups,
    (group) => ({
      key: group.id,
      name: i18n.getString(group.name),
      items: _.chain(group.items)
        .map((id) => _.find(siteMap, (item) => item.id === id))
        .filter((item) => !_.isUndefined(item) && item.searchable !== false)
        .map((item) => {
          const route = getSiteMapItemRoute({ item, scope, userContext });
          const isPinned = (item.pinned || item.fixed) && !route.isExternal;
          const isPinnable = !route.isExternal;

          return ({
            key: item.id,
            name: i18n.getString(item.name),
            description: i18n.getString(item.description),

            isPinned,
            isUnpinnable: !item.fixed && !route.isExternal,
            isPinnable,
            route: route.href,
            isExternal: route.isExternal,

            onTogglePin: () => item.onPinToggle(isNavRefresh2Enabled ? 'tools-panel' : 'tools-dialog'),
            onClick: () => onClick({
              id: item.id,
              isPinned: !!isPinned,
              route: route.href,
              isPinnable,
            }),
          });
        })
        .value(),
    }),
  );

  return {
    groups,
  };
};
